import { AppIcon } from '@/components/ui/app-icon';
import { CopilotIcon } from '@/components/ui/copilot-icon.component';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { useCopilotAccess } from '@/hooks/use-copilot-access.hook';
import { iconArrowUp, iconSpinnerThird } from '@/lib/icons';

const PLACEHOLDER_SUGGESTIONS = [
  'Founders in Toronto at 51-200 employee companies',
  'VC-backed SaaS CEOs in Canada',
  'Series A fintech founders',
  'AI startup co-founders in New York',
  'Healthcare startup CEOs',
  'E-commerce founders with 10-50 employees',
];

interface ChatSearchBoxProps {
  onSearch: (query: string, useWebSearch?: boolean) => void;
  isSearching: boolean;
  compact?: boolean;
}

export const ChatSearchBox = ({
  onSearch,
  isSearching,
  compact = false,
}: ChatSearchBoxProps) => {
  const [query, setQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Render nothing only for AppSumo-only orgs (AppSumo present +
  // tier_category="free" from /billing/feature-access). Free,
  // Cleanlist-paid, and stacked all keep the composer. BE 403s
  // /agent/chat for AppSumo-only as a hard backstop and serves a
  // cheaper explainer Copilot for Free.
  const { canUseCopilot } = useCopilotAccess();

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setPlaceholderIndex(
          (prev) => (prev + 1) % PLACEHOLDER_SUGGESTIONS.length
        );
        setIsAnimating(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (query.trim() && !isSearching) {
      onSearch(query.trim());
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  if (!canUseCopilot) return null;

  if (compact) {
    return (
      <form onSubmit={handleSubmit}>
        <div className='overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-[#303030]'>
          <div className='p-3'>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Ask anything'
              rows={1}
              className='w-full resize-none bg-transparent text-[15px] text-gray-900 placeholder:text-gray-500 focus:outline-hidden disabled:opacity-50 dark:text-white'
              disabled={isSearching}
            />
          </div>
          <div className='flex items-center justify-end border-t border-gray-100 px-3 py-2 dark:border-white/5'>
            <button
              type='submit'
              disabled={!query.trim() || isSearching}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full transition-all',
                query.trim() && !isSearching
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-gray-200 text-gray-400 dark:bg-[#424242] dark:text-gray-500'
              )}
            >
              {isSearching ? (
                <AppIcon
                  icon={iconSpinnerThird}
                  className='animate-spin'
                  style={{ fontSize: 16 }}
                />
              ) : (
                <AppIcon icon={iconArrowUp} className='h-4 w-4' />
              )}
            </button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div className='mx-auto w-full max-w-2xl'>
      <div className='mb-3 flex items-center justify-center gap-2'>
        <CopilotIcon className='h-6 w-6' animate='always' />
        <h2 className='text-lg font-semibold tracking-tight text-gray-900 dark:text-white'>
          Prospect with Clu
        </h2>
        <span className='rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'>
          Beta
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className='relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white dark:border-white/6 dark:bg-[#303030]'>
          <div className='px-4 pt-4 pb-2'>
            <div className='relative'>
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder=''
                rows={2}
                className='w-full resize-none bg-transparent text-sm leading-relaxed text-gray-900 placeholder:text-gray-500 focus:outline-hidden disabled:opacity-50 dark:text-white'
                disabled={isSearching}
              />
              {!query && (
                <div
                  className={cn(
                    'pointer-events-none absolute top-0 left-0 text-sm leading-relaxed text-gray-400 transition-opacity duration-300 dark:text-gray-500',
                    isAnimating ? 'opacity-0' : 'opacity-100'
                  )}
                >
                  {PLACEHOLDER_SUGGESTIONS[placeholderIndex]}
                </div>
              )}
            </div>
          </div>

          {/* Bottom toolbar - send button on right (matches Find People search send button) */}
          <div className='flex items-center justify-end px-3 pb-3'>
            <button
              type='submit'
              disabled={!query.trim() || isSearching}
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] transition-colors',
                query.trim() && !isSearching
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-gray-200 text-gray-400 dark:bg-[#424242] dark:text-gray-500'
              )}
            >
              {isSearching ? (
                <AppIcon
                  icon={iconSpinnerThird}
                  className='animate-spin'
                  style={{ fontSize: 16 }}
                />
              ) : (
                <AppIcon icon={iconArrowUp} style={{ fontSize: 16 }} />
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
